<template>
  <div class="wg-all-content">
    <div
      v-if="loading"
      class="wg-all-content__skeleton"
      aria-busy="true"
      aria-label="Loading content"
    >
      <div class="wg-all-content__skeleton-filters">
        <div v-for="n in 4" :key="'f-' + n" class="wg-all-content__skeleton-pill" />
      </div>
      <div class="wg-all-content__skeleton-table">
        <div class="wg-all-content__skeleton-thead">
          <div
            v-for="col in skeletonColCount"
            :key="'h-' + col"
            class="wg-all-content__skeleton-cell"
          />
        </div>
        <div v-for="row in 6" :key="'r-' + row" class="wg-all-content__skeleton-trow">
          <div
            v-for="col in skeletonColCount"
            :key="row + '-' + col"
            class="wg-all-content__skeleton-cell"
          />
        </div>
      </div>
    </div>

    <template v-else>
      <EmptyUI v-if="allRows.length === 0" :cards="['content']" />

      <template v-else>
        <div class="wg-all-content__filters" role="tablist" aria-label="Filter by status">
          <button
            v-for="pill in filterPills"
            :key="pill.value"
            type="button"
            role="tab"
            class="wg-all-content__pill"
            :class="{ 'wg-all-content__pill--active': statusFilter === pill.value }"
            :aria-selected="statusFilter === pill.value"
            @click="statusFilter = pill.value"
          >
            {{ pill.label }}
            <span class="wg-all-content__pill-count">{{ pill.count }}</span>
          </button>
        </div>

        <div v-if="filteredRows.length === 0" class="wg-all-content__empty-filter">
          No content matches this filter.
        </div>

        <div v-else class="wg-all-content__table-wrap">
          <table class="wg-all-content__table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Source</th>
                <th>Keyword</th>
                <th>Content type</th>
                <th>Order date</th>
                <th>Delivery period</th>
                <th>Delivery due date</th>
                <th>Price</th>
                <th class="text-center">Status</th>
                <th v-if="showAdminOrderControls" class="text-center">Commission</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredRows" :key="row.rowKey">
                <td>
                  <router-link
                    v-if="row.orderPath"
                    :to="row.orderPath"
                    class="wg-all-content__order-id"
                  >
                    #{{ row.orderId }}
                  </router-link>
                  <span v-else class="wg-all-content__order-id">#{{ row.orderId }}</span>
                </td>
                <td>{{ row.sourceLabel }}</td>
                <td>{{ row.keywordLabel }}</td>
                <td>{{ row.contentTypeLabel }}</td>
                <td>{{ row.dateLabel }}</td>
                <td>{{ row.deliveryPeriodLabel }}</td>
                <td>{{ row.deliveryDueDateLabel }}</td>
                <td style="font-weight: 700;">{{ row.priceLabel }}</td>
                <td class="text-center wg-all-content__status-cell">
                  <b-dropdown
                    v-if="showAdminOrderControls && canEditRowFulfillment(row)"
                    aria-role="list"
                    class="wg-all-content__mc-dropdown wg-all-content__mc-dropdown--status"
                    style="text-align: left;"
                  >
                    <template #trigger>
                      <button
                        type="button"
                        class="wg-all-content__mc-trigger"
                        :class="statusDropdownTriggerClass(row)"
                      >
                        <span>{{ statusLabel(row.fulfillementStatus) }}</span>
                        <i class="material-icons-outlined wg-all-content__mc-trigger__caret">expand_more</i>
                      </button>
                    </template>
                    <b-dropdown-item
                      v-if="row.fulfillementStatus !== 'pending'"
                      aria-role="listitem"
                      @click="patchContentOrder(row, { set_pending: true })"
                    >
                      Mark as {{ getFulfillementStatusLabel('pending') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="row.fulfillementStatus !== 'in_progress'"
                      aria-role="listitem"
                      @click="patchContentOrder(row, { set_in_progress: true })"
                    >
                      Mark as {{ getFulfillementStatusLabel('in_progress') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="row.fulfillementStatus !== 'completed'"
                      aria-role="listitem"
                      @click="patchContentOrder(row, { set_completed: true })"
                    >
                      Mark as {{ getFulfillementStatusLabel('completed') }}
                    </b-dropdown-item>
                  </b-dropdown>
                  <span
                    v-else
                    class="wg-all-content__status"
                    :class="statusBadgeClass(row.fulfillementStatus)"
                  >
                    {{ statusLabel(row.fulfillementStatus) }}
                  </span>
                </td>
                <td v-if="showAdminOrderControls" class="text-center wg-all-content__commission-cell">
                  <b-dropdown
                    v-if="!row.includedInPackage"
                    aria-role="list"
                    class="wg-all-content__mc-dropdown wg-all-content__mc-dropdown--sales"
                    style="text-align: left;"
                  >
                    <template #trigger>
                      <button
                        type="button"
                        class="wg-all-content__mc-trigger"
                        :class="rowSalesTagTriggerClass(row)"
                      >
                        <span>{{ rowSalesTagLabel(row) }}</span>
                        <i class="material-icons-outlined wg-all-content__mc-trigger__caret">expand_more</i>
                      </button>
                    </template>
                    <b-dropdown-item
                      :disabled="row.sales_tag === 'passive'"
                      aria-role="listitem"
                      @click="patchContentOrder(row, { sales_tag: 'passive' })"
                    >
                      Set as passive
                    </b-dropdown-item>
                    <b-dropdown-item
                      :disabled="row.sales_tag === 'active'"
                      aria-role="listitem"
                      @click="patchContentOrder(row, { sales_tag: 'active' })"
                    >
                      Set as <b>active</b>
                    </b-dropdown-item>
                  </b-dropdown>
                </td>
                <td class="wg-all-content__action-cell text-center">

                  <div class="d-flex justify-center align-center gap10">

                    <!--
                    <button
                      v-if="showAdminOrderControls && row.fulfillementStatus !== 'completed' && canEditRowFulfillment(row)"
                      type="button"
                      class="wg-all-content__mark-completed-link"
                      @click="patchContentOrder(row, { set_completed: true })"
                    >
                      Mark as completed
                    </button>
                    -->
  
                    <template 
                      v-if="(
                        (row.siloKeyword?.pageRunId && row.siloKeyword?.pageRunResult?.status === 'SUCCESS')
                        && (
                          row.fulfillementStatus === 'completed'
                          || (
                            row.fulfillementStatus !== 'completed' && (isMissionControlSession && isWhiteGloveAdminMode)
                          )
                        )
                      )"
                    >
  
                      <v-btn
                        text
                        small
                        color="profileAnchorColor"
                        :ripple="false"
                        class="text-none"
                        @click="handleViewContent(row.siloKeyword, row.order)"
                      >
                        <span class="font-weight-bold">View content</span>
                      </v-btn>

                      <silo-action-menu
                        :ref="'silo-action-menu-' + row.siloKeywordId"
                        :siloKeyword="row.siloKeyword"
                        :silo="row?.order?.silo"
                        :isWhiteGloveUser="isWhiteGloveUser"
                        :isMissionControlSession="isMissionControlSession && isWhiteGloveAdminMode"
                        :isOrderedReport="true"
                        :orderedReportType="getContentReportTypeId(row.siloKeyword, row.order)"
                        @refreshData="() => {}"
                      />

                    </template>

                    <span
                      v-else
                      class="wg-all-content__pending"
                    >
                      Pending
                    </span>

                  </div>

                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { formatCurrency, getPureServicePkgName } from '@/utils'
import { ALLOWED_SILO_ORDER_DELIVERY_PERIODS, whiteGlovePlans } from '@/components/plans_constants'
import { popAuthorityOrderIsIncluded, popAuthorityPackageMonthLabel } from '@/utils/popAuthority'
import { getFulfillementStatusLabel } from '@/components/Campaigns/content-orders/utils.js'
import EmptyUI from './EmptyUI.vue'
import SiloActionMenu from '@/components/Campaigns/silo/SiloActionMenu.vue'

const CONTENT_ORDER_TYPES = ['silo_order', 'quick_content_order']

function checkoutUnitAmount (order, tier) {
  const items = order.items_on_checkout || []
  const want = tier === 'top_level' ? 'top_level' : 'supporting'
  const line = items.find((i) => i.type === want)
  return line != null ? line.unit_amount : null
}

/** Line items per keyword; supports API `keyword_line_items` or legacy id-only orders. */
function keywordLinesForOrder (order) {
  const existing = order.keyword_line_items
  if (Array.isArray(existing) && existing.length) {
    return existing.map((line) => ({
      siloKeywordId: line.siloKeywordId,
      keyword: line.keyword,
      tier: line.tier,
      contentOptionKey: line.contentOptionKey,
      unitPrice:
        line.unitPrice != null
          ? line.unitPrice
          : checkoutUnitAmount(order, line.tier),
      pageRunId: line.pageRunId,
      pageId: line.pageId,
      siteId: line.siteId,
      aiArticleReady: Boolean(line.aiArticleReady),
    }))
  }
  const lines = []
  for (const kid of order.selected_top_level_keyword_ids || []) {
    lines.push({
      siloKeywordId: kid,
      keyword: null,
      tier: 'top_level',
      contentOptionKey: order.selected_top_level_option_key,
      unitPrice: checkoutUnitAmount(order, 'top_level'),
    })
  }
  for (const kid of order.selected_supporting_keyword_ids || []) {
    lines.push({
      siloKeywordId: kid,
      keyword: null,
      tier: 'supporting',
      contentOptionKey: order.selected_supporting_option_key,
      unitPrice: checkoutUnitAmount(order, 'supporting'),
    })
  }
  return lines
}

function formatOrderDate (order) {
  const ts = order.order_date_timestamp
  if (ts) return moment.unix(ts).format('MMM. D, YYYY')
  if (!order.order_date) return '—'
  return moment(order.order_date).isValid()
    ? moment(order.order_date).format('MMM. D, YYYY')
    : order.order_date
}

function formatDeliveryPeriodLabel (order) {
  const days = order.delivery_period_days
  if (days == null) return '—'
  const entry = ALLOWED_SILO_ORDER_DELIVERY_PERIODS[days]
  return entry?.label || '—'
}

function formatDeliveryDueDate (value) {
  if (!value) return '—'
  const m = moment.utc(value)
  return m.isValid() ? m.format('MMM. D, YYYY') : String(value)
}

export default {
  name: 'AllContentWhiteGlove',
  components: { EmptyUI, SiloActionMenu },
  inject: {
    refreshWhiteGloveOrders: {
      default: null,
    },
  },
  data () {
    return {
      loading: true,
      siteOrders: [],
      siloPackages: [],
      statusFilter: 'all',
    }
  },
  computed: {
    siteId () {
      return Number(this.$route.params.siteId)
    },
    siteContentOrders () {
      return this.siteOrders.filter(
        (o) => o.site_id === this.siteId && CONTENT_ORDER_TYPES.includes(o.type)
      )
    },
    packageByKey () {
      const map = {}
      for (const p of this.siloPackages || []) {
        if (p && p.key != null) {
          map[p.key] = p
        }
      }
      return map
    },
    allRows () {
      const rows = []
      for (const order of this.siteContentOrders) {
        const lines = keywordLinesForOrder(order)
        const planName = order.silo?.name || order.orderLabel || '—'
        const sourceLabel =
          order.type === 'quick_content_order'
            ? 'Content only'
            : `Content plan: ${planName}`
        const dateLabel = formatOrderDate(order)
        // POP Authority: produced by a paid package month (no per-order charge)
        const includedInPackage = popAuthorityOrderIsIncluded(order)
        const deliveryPeriodLabel = (includedInPackage && order.delivery_period_days == null)
          ? popAuthorityPackageMonthLabel(order)
          : formatDeliveryPeriodLabel(order)
        const deliveryDueDateLabel = formatDeliveryDueDate(order.due_date)
        const st = order.fulfillement_status || order.fulfillementStatus || ''
        const orderPath = this.getOrderPath(order)
        const sortTs = order.order_date_timestamp || 0

        for (const line of lines) {
          const key = line.contentOptionKey
          const pkg = key ? this.packageByKey[key] : null
          const contentTypeLabel =
            pkg?.label ||
            (key ? String(key).replace(/_/g, ' ') : '—')
          const kw =
            line.keyword ||
            (line.siloKeywordId != null ? `Keyword #${line.siloKeywordId}` : '—')
          rows.push({
            rowKey: `${order.id}-${line.siloKeywordId}-${line.tier}`,
            siloKeywordId: line.siloKeywordId,
            siloKeyword: order.silo?.silo_keywords?.find((k) => k.id === line.siloKeywordId),
            order,
            orderId: order.id,
            order_status: order.order_status,
            sales_tag: order.sales_tag,
            isQuickContentOrder: order.type === 'quick_content_order',
            sourceLabel,
            keywordLabel: kw,
            contentTypeLabel,
            dateLabel,
            deliveryPeriodLabel,
            deliveryDueDateLabel,
            priceLabel: includedInPackage
              ? 'Included'
              : (line.unitPrice != null && line.unitPrice !== ''
                ? formatCurrency(line.unitPrice)
                : '—'),
            includedInPackage,
            fulfillementStatus: st,
            orderPath,
            sortTs,
            pageRunId: line.pageRunId,
            pageId: line.pageId,
            siteId: line.siteId,
            aiArticleReady: Boolean(line.aiArticleReady),
          })
        }
      }
      rows.sort((a, b) => {
        if (b.sortTs !== a.sortTs) return b.sortTs - a.sortTs
        return String(a.keywordLabel).localeCompare(String(b.keywordLabel))
      })
      return rows
    },
    filterPills () {
      const all = this.allRows
      const count = (pred) => all.filter(pred).length
      return [
        { value: 'all', label: 'All', count: all.length },
        {
          value: 'pending',
          label: 'Ordered',
          count: count((r) => r.fulfillementStatus === 'pending'),
        },
        {
          value: 'in_progress',
          label: 'In progress',
          count: count((r) => r.fulfillementStatus === 'in_progress'),
        },
        {
          value: 'completed',
          label: 'Completed',
          count: count((r) => r.fulfillementStatus === 'completed'),
        },
      ]
    },
    filteredRows () {
      if (this.statusFilter === 'all') {
        return this.allRows
      }
      return this.allRows.filter((r) => r.fulfillementStatus === this.statusFilter)
    },
    user () {
      return this.$store.state.user
    },
    selectedSubAccountRelation() {
      return this.$store.state.selectedSubAccountRelation
    },
    isWhiteGloveUser () {
      if (!this.user) return false
      const accountType = this.$store.state.selectedSubAccountRelation
        ? this.$store.state.selectedSubAccountRelation.ownerAccountType
        : this.user.accountType
      return whiteGlovePlans.includes(getPureServicePkgName(accountType))
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isWhiteGloveAdminMode () {
      return this.$store.state.isWhiteGloveAdminMode
    },
    showAdminOrderControls () {
      return this.isWhiteGloveUser && this.isMissionControlSession && this.isWhiteGloveAdminMode
    },
    skeletonColCount () {
      return this.showAdminOrderControls ? 11 : 10
    },
  },
  watch: {
    '$store.state.triggerRockyRefresh': function () {
      this.handleRockyRefresh()
    },
  },
  mounted () {
    this.loadData()
  },
  methods: {
    getFulfillementStatusLabel,
    handleRockyRefresh () {
      this.loadData({ silent: true })
    },
    loadData (opts = {}) {
      const silent = opts.silent === true
      if (!silent) this.loading = true
      return Promise.all([
        this.$store.dispatch('getWhiteGloveOrders', {
          includeKeywordLineItems: true,
          include_keywords: true,
          include_ai_and_page_run_result: true,
        }),
        this.$store.dispatch('getAvailableSiloContentReportPackages'),
      ])
        .then(([ordersRes, pkgRes]) => {
          this.siteOrders = ordersRes.data.data || []
          this.siloPackages = pkgRes.data.packages || []
        })
        .catch(() => {
          if (!silent) {
            this.siteOrders = []
            this.siloPackages = []
          }
        })
        .finally(() => {
          if (!silent) this.loading = false
        })
    },
    canEditRowFulfillment (row) {
      return row.order_status === 'paid'
    },
    statusDropdownTriggerClass (row) {
      const fs = row.fulfillementStatus || ''
      if (fs === 'completed') return 'wg-all-content__mc-trigger--status-done'
      if (fs === 'in_progress') return 'wg-all-content__mc-trigger--status-progress'
      if (fs === 'pending') return 'wg-all-content__mc-trigger--status-pending'
      if (fs) return 'wg-all-content__mc-trigger--status-other'
      return 'wg-all-content__mc-trigger--status-pending'
    },
    rowSalesTagLabel (row) {
      return row.sales_tag === 'active' ? 'Active' : 'Passive'
    },
    rowSalesTagTriggerClass (row) {
      return row.sales_tag === 'active'
        ? 'wg-all-content__mc-trigger--sales-active'
        : 'wg-all-content__mc-trigger--sales-passive'
    },
    shouldRefreshWhiteGloveSiteStatus (body) {
      return Boolean(body.set_pending || body.set_in_progress || body.set_completed)
    },
    patchContentOrder (row, body) {
      this.$store.commit('showLoading')
      this.$store
        .dispatch('updateSiloContentReportOrder', {
          orderId: row.orderId,
          body,
        })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            const refreshTasks = [this.loadData({ silent: true })]
            if (
              this.shouldRefreshWhiteGloveSiteStatus(body)
              && typeof this.refreshWhiteGloveOrders === 'function'
            ) {
              refreshTasks.push(this.refreshWhiteGloveOrders())
            }
            return Promise.all(refreshTasks)
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    getOrderPath (order) {
      if (order.type === 'silo_order' && order.silo) {
        return {
          name: 'ContentPlanDetailWhiteGlove',
          params: {
            siteId: String(order.silo.siteId),
            siloId: String(order.siloId),
          },
        }
      }
      if (order.type === 'quick_content_order') {
        const siteId = order.silo?.siteId ?? order.site_id
        const siloId = order.siloId ?? order.silo?.id
        if (siteId != null && siloId != null) {
          return {
            name: 'ContentOnlyDetailWhiteGlove',
            params: {
              siteId: String(siteId),
              siloId: String(siloId),
            },
          }
        }
      }
      return null
    },
    handleViewContent(siloKeyword, order) {
      // NOTE: any updates to this function should be reflected in the Campaigns/silo/SiloResultTable.vue handleViewContent function
      if (
        siloKeyword.keywordData.isTopLevelKeyword
        && order?.selected_top_level_option_key === 'TOP_LEVEL_CONTENT_UPGRADE'
      ) {
        this.$refs['silo-action-menu-' + siloKeyword.id][0].getSiloTopLevelReport()
        return null
      }

      let secretKey = siloKeyword.pageRunResult.secretKey;
      if (!secretKey) return;
      let url = `/content-editor?secretKey=${secretKey}&siloKeywordId=${siloKeyword.id}`
      if (this.selectedSubAccountRelation) {
        url += `&relId=${this.selectedSubAccountRelation.id}`
      }
      url += `&isWhiteGloveUser=true&isOrderedReport=true&orderedReportType=${this.getContentReportTypeId(siloKeyword, order)}`
      // open in new tab
      window.open('/#' + url, '_blank')
      return null
    },
    statusLabel (status) {
      if (status === 'pending') return 'Ordered'
      if (status === 'in_progress') return 'In progress'
      if (status === 'completed') return 'Completed'
      if (!status) return '—'
      return String(status).replace(/_/g, ' ')
    },
    statusBadgeClass (status) {
      return {
        'wg-all-content__status--ordered': status === 'pending',
        'wg-all-content__status--progress': status === 'in_progress',
        'wg-all-content__status--completed': status === 'completed',
        'wg-all-content__status--other':
          status && !['pending', 'in_progress', 'completed'].includes(status),
      }
    },
    getContentReportTypeId(siloKeyword, order) {
      let reportKey = null
      if (siloKeyword.keywordData.isTopLevelKeyword) {
        reportKey = order?.selected_top_level_option_key
      } else if (siloKeyword.keywordData.isSupportingKeyword) {
        reportKey = order?.selected_supporting_option_key
      }
      return this.siloPackages.filter(p => p.key === reportKey)?.[0]?.report_id
    }
  },
}
</script>

<style lang="scss" scoped>
@keyframes wg-all-content-skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
}

.wg-all-content__skeleton {
  padding: 4px 0 8px;
}

.wg-all-content__skeleton-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.wg-all-content__skeleton-pill {
  height: 34px;
  width: 120px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    #e8e9ed 0%,
    #f2f3f6 45%,
    #e8e9ed 90%
  );
  background-size: 200% 100%;
  animation: wg-all-content-skeleton-shimmer 1.35s ease-in-out infinite;
}

.wg-all-content__skeleton-table {
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 12px;
  background: #fff;
}

.wg-all-content__skeleton-thead {
  display: flex;
  gap: 8px;
  padding: 14px 16px;
  background: #f0f1f5;
  border-bottom: 1px solid var(--v-gray12Color-base);
}

.wg-all-content__skeleton-trow {
  display: flex;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid #ececf0;

  &:nth-child(2n + 3) {
    background: #fafafc;
  }

  &:last-child {
    border-bottom: none;
  }
}

.wg-all-content__skeleton-cell {
  flex: 1;
  min-width: 0;
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    #e8e9ed 0%,
    #f2f3f6 45%,
    #e8e9ed 90%
  );
  background-size: 200% 100%;
  animation: wg-all-content-skeleton-shimmer 1.35s ease-in-out infinite;

  &:nth-child(8) {
    flex: 0.45;
  }

  &:nth-child(9) {
    flex: 0.5;
  }
}

.wg-all-content__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.wg-all-content__pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid var(--v-gray12Color-base);
  background: #fff;
  color: var(--v-mainColor-base);
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    border-color: var(--v-profileAnchorColor-base);
    color: var(--v-profileAnchorColor-base);
  }

  &--active {
    background: #4a4f59;
    color: #fff;
    border-color: #4a4f59;

    &:hover {
      color: #fff;
      border-color: #4a4f59;
    }

    .wg-all-content__pill-count {
      color: #444;
    }
  }
}

.wg-all-content__pill-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f7f5fb;
  padding: 2px 10px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.7rem;
}

.wg-all-content__empty-filter {
  padding: 24px 16px;
  text-align: center;
  color: var(--v-gray60Color-base, #666);
  font-size: 0.95rem;
}

.wg-all-content__table-wrap {
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 12px;
  background: #fff;
}

.wg-all-content__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  thead th {
    text-align: left;
    padding: 14px 16px;
    background: #f0f1f5;
    font-weight: 600;
    color: var(--v-mainColor-base);
    border-bottom: 1px solid var(--v-gray12Color-base);
  }

  tbody td {
    padding: 14px 16px;
    color: var(--v-mainColor-base);
    border-bottom: 1px solid #ececf0;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:nth-child(even) {
    background: #fafafc;
  }
}

.wg-all-content__order-id {
  font-weight: 700;
  color: var(--v-mainColor-base);
}

a.wg-all-content__order-link {
  color: var(--v-profileAnchorColor-base);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.wg-all-content__status {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  text-wrap: nowrap;
}

.wg-all-content__status--ordered {
  background: #FF57001A;
  color: #FF5700;
}

.wg-all-content__status--progress {
  background: #EF34A41A;
  color: #EF34A4;
}

.wg-all-content__status--completed {
  background: #34A8531A;
  color: #11A33D;
}

.wg-all-content__status--other {
  background: #8439FF1A;
  color: #8439FF;
}

.wg-all-content__action-cell {
  text-align: right;
  white-space: nowrap;
}

.wg-all-content__mark-completed-link {
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-mainColor-base);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.wg-all-content__pending {
  color: var(--v-gray60Color-base, #666);
  font-weight: 600;
  font-size: 0.9rem;
}

.wg-all-content__source-link {
  font-weight: 600;
  color: var(--v-profileAnchorColor-base);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.wg-all-content__view-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  color: var(--v-profileAnchorColor-base);
  text-decoration: none;

  &:hover,
  &:focus,
  &:visited {
    text-decoration: none;
    color: var(--v-profileAnchorColor-base);
  }
}

.wg-all-content__view-icon {
  font-size: 18px;
}

.wg-all-content__status-cell,
.wg-all-content__commission-cell {
  vertical-align: middle;
}

::v-deep .wg-all-content__mc-dropdown .dropdown-trigger {
  display: inline-flex;
}

::v-deep .wg-all-content__mc-dropdown .dropdown-menu {
  min-width: 200px;
}

.wg-all-content__mc-trigger {
  min-width: 108px;
  height: 28px;
  padding: 0 10px 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  line-height: 1.2;
}

.wg-all-content__mc-dropdown--status .wg-all-content__mc-trigger {
  min-width: 128px;
}

.wg-all-content__mc-trigger__caret {
  flex-shrink: 0;
  font-size: 1.125rem !important;
  color: rgba(0, 0, 0, 0.45);
}

.wg-all-content__mc-trigger--status-progress {
  color: #ef34a4;
}

.wg-all-content__mc-trigger--status-done {
  color: #11a33d;
}

.wg-all-content__mc-trigger--status-pending {
  color: #ff5700;
}

.wg-all-content__mc-trigger--status-other {
  color: #8439ff;
}

.wg-all-content__mc-trigger--sales-active {
  color: var(--v-mainColor-base);
}

.wg-all-content__mc-trigger--sales-passive {
  color: var(--v-mainColor-base);
}
</style>
